import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import RefreshIcon from "@mui/icons-material/Refresh";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { HorizontalScrollTable } from "@/components/molecules/HorizontalScrollTable";
import { useAdminUsers } from "../../hooks/admin/use-admin-users";
import { useTenants } from "../../hooks/admin/use-tenants";
import {
  useAdminInvitations,
  useResendInvitation,
  useCancelInvitation,
} from "../../hooks/admin/use-admin-invitations";
import { useImpersonateUser } from "../../hooks/admin/use-impersonation";
import { InviteUserForm } from "../../components/admin/InviteUserForm";

const roleColorMap: Record<string, "secondary" | "primary" | "default"> = {
  super_admin: "secondary",
  admin: "primary",
  user: "default",
};

const invitationStatusColorMap: Record<
  string,
  "warning" | "success" | "error" | "default"
> = {
  pending: "warning",
  accepted: "success",
  canceled: "error",
  expired: "default",
};

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-NZ");
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return `${date.toLocaleDateString("en-NZ")} ${date.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}`;
}

export function AdminUsers() {
  const [selectedTenant, setSelectedTenant] = useState<string | undefined>();
  const [showInviteForm, setShowInviteForm] = useState(false);
  const { data: users, isLoading: usersLoading } =
    useAdminUsers(selectedTenant);
  const { data: tenants } = useTenants();
  const { data: invitations, isLoading: invitationsLoading } =
    useAdminInvitations(selectedTenant);

  const resendMutation = useResendInvitation();
  const cancelMutation = useCancelInvitation();
  const impersonateMutation = useImpersonateUser();

  const handleResend = (invitationId: string) => {
    resendMutation.mutate(invitationId);
  };

  const handleCancel = (invitationId: string) => {
    if (window.confirm("Are you sure you want to cancel this invitation?")) {
      cancelMutation.mutate(invitationId);
    }
  };

  // Filter to only show pending/expired invitations (not accepted/canceled)
  const pendingInvitations = invitations?.filter(
    (inv) => inv.status === "pending" || inv.status === "expired",
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage system users and invitations
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Tenant</InputLabel>
            <Select
              value={selectedTenant ?? ""}
              onChange={(e) => setSelectedTenant(e.target.value || undefined)}
              label="Tenant"
            >
              <MenuItem value="">All Tenants</MenuItem>
              {tenants?.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={() => setShowInviteForm(!showInviteForm)}
          >
            {showInviteForm ? "Cancel" : "Invite User"}
          </Button>
        </Box>
      </Box>

      {showInviteForm && (
        <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Invite New User
          </Typography>
          <InviteUserForm onSuccess={() => setShowInviteForm(false)} />
        </Card>
      )}

      {/* Pending Invitations Section */}
      {(invitationsLoading ||
        (pendingInvitations && pendingInvitations.length > 0)) && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            fontWeight={600}
            color="text.primary"
            sx={{ mb: 2 }}
          >
            Pending Invitations
          </Typography>

          {invitationsLoading ? (
            <Skeleton variant="rounded" height={120} />
          ) : (
            <Card variant="outlined" sx={{ overflow: "hidden" }}>
              <HorizontalScrollTable minWidth={800}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Organization</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Invited By</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingInvitations?.map((invitation) => (
                      <TableRow key={invitation.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {invitation.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {invitation.organizationName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invitation.role}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invitation.status}
                            color={
                              invitationStatusColorMap[invitation.status] ??
                              "default"
                            }
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {invitation.inviterName ||
                              invitation.inviterEmail ||
                              "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color={
                              invitation.status === "expired"
                                ? "error.main"
                                : "text.secondary"
                            }
                          >
                            {formatDateTime(invitation.expiresAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Resend invitation">
                            <IconButton
                              size="small"
                              onClick={() => handleResend(invitation.id)}
                              disabled={resendMutation.isPending}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel invitation">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleCancel(invitation.id)}
                              disabled={cancelMutation.isPending}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </HorizontalScrollTable>
            </Card>
          )}
        </Box>
      )}

      {/* Active Users Section */}
      <Typography
        variant="h6"
        fontWeight={600}
        color="text.primary"
        sx={{ mb: 2 }}
      >
        Active Users
      </Typography>

      {usersLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={64} />
          ))}
        </Box>
      ) : (
        <Card variant="outlined" sx={{ overflow: "hidden" }}>
          <HorizontalScrollTable minWidth={700}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users?.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Typography variant="body2">{user.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.firstName || user.lastName
                          ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        color={roleColorMap[user.role] ?? "default"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? "Active" : "Suspended"}
                        color={user.isActive ? "success" : "error"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.lastLoginAt
                          ? formatDate(user.lastLoginAt)
                          : "Never"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={
                          user.role === "super_admin"
                            ? "Cannot impersonate super_admin users"
                            : "View as this user"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => impersonateMutation.mutate(user.id)}
                            disabled={
                              user.role === "super_admin" ||
                              impersonateMutation.isPending
                            }
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </HorizontalScrollTable>
        </Card>
      )}
    </Box>
  );
}
